import { AxiosResponse } from "axios";
import {
  AltiusRes,
  CreateFileRes,
  DataroomFilesFilters,
  DateRange,
  FileFiltersToReq,
  FileRepoFilters,
  GetFileData,
  IApproveMeasureFilters,
  IBulkUpdateFilesRepoData,
  ICloudMirrorUploadItem,
  IDataExtractionTag,
  IDownloadFileData,
  IFile,
  IFileMeta,
  IFileMetaDetail,
  IFileRepo,
  IGetDRFilesQueries,
  IGetFileRepo,
  IHandleItemEventType,
  IMoveFileData,
  IRecommendationData,
  IRecommendationDataFilters,
  IRecommendationItem,
  IUpdateFileData,
  IUpdateFileTagData,
  IUploadFileData,
  PagedDataroomFiles,
  PagedRecommendations,
  Polygons
} from "store/types";
import { formatDate, toFormData } from "utils";
import downloadFileAxios from "utils/download-file-axios";
import { v4 as uuidv4 } from "uuid";

import { DealAction, UserAction } from "..";
import { AsyncActions, AsyncActionsParams } from "../base";

type DownloadDRFile = "blob" | "object";

export interface IFileActions {
  getFilesAction: (dealId: number) => Promise<void>;
  getFileAction: (data: GetFileData) => Promise<IFile>;

  getAllFilesByDRAction: (queries: IGetDRFilesQueries) => Promise<PagedDataroomFiles>;

  downloadFileAction: (data: IDownloadFileData) => Promise<void>;
  uploadFileAction: (data: IUploadFileData) => Promise<IFile>;
  uploadCloudMirrorSectionFilesAction: (items: ICloudMirrorUploadItem[]) => Promise<void>;
  uploadDashboardFileAction: (data: IUploadFileData) => Promise<string | undefined>;
  dispatchFileAction: (file: IFile) => void;

  moveFileAction: (data: IMoveFileData) => Promise<IFile>;
  // moveFileFromEmailAction: (data: IMoveFileData) => Promise<IFile>;
  updateFileAction: (data: IUpdateFileData) => Promise<IFile>;
  updateFileRepoAction: (data: IUpdateFileData) => Promise<IFileRepo>;

  deleteFileAction: (data: IFile) => Promise<void>;

  getFileMetaAction: (dealId: number, fileId: number) => Promise<void>;
  updateFileMetaAction: (fileMeta: Partial<IFileMeta>) => void;
  invalidateFileMetaAction: () => void;
  handleFile: (file: IFile, event: IHandleItemEventType) => void;
  downloadFileFromDRAction: <R extends DownloadDRFile | undefined = undefined>(data: {
    dealId: number;
    fileId: number;
    response_type?: R;
  }) => Promise<R extends "object" | undefined ? IFile : void>;

  uploadDirectoryAction: (data: { files: FileList; deal_id: number; section_id?: number }) => Promise<void>;

  uploadFilesWithAiAction: (data: {
    dealId: number;
    files: FileList | File[];
    aiExtraction?: boolean;
    isK1Extraction?: boolean;
  }) => Promise<void>;
  fileAiExtractionAction: (file_ids: number[]) => Promise<void>;
  extractFileK1Action: (id: number) => Promise<void>;
  getFilesRepoAction: (
    data: IGetFileRepo
  ) => Promise<{ total: number; last_page: number; current_page: string; files: IFileRepo[] }>;
  downloadFilesRepoAction: (file_ids: number[]) => Promise<void>;
  createFileRepoTaggerLinkAction: (file_ids: number[]) => Promise<void>;
  extractFileRepoK1Action: (ids: number[]) => Promise<void>;
  getFilePolygonsAction: (fileId: number) => Promise<Polygons>;
  getFileTagsAction: (fileId: number) => Promise<IDataExtractionTag[]>;
  createFileTagAction: (
    fileId: number,
    data: { measure_id: number; value: number; polygon_id: string }
  ) => Promise<IDataExtractionTag>;
  reRunFileExtractionAction: ({ file_id, prompt }: { file_id: number; prompt: string }) => Promise<void>;
  updateFileTagAction: (fileId: number, tagId: number, data: IUpdateFileTagData) => Promise<IDataExtractionTag>;
  deleteFileTagAction: (fileId: number, tagId: number) => Promise<void>;
  saveFileTagsAction: (fileId: number) => Promise<void>;
  getFileByIdAction: (fileId: number) => Promise<IFileRepo>;
  checkFilesVerifiedAction: (fileIds: number[]) => Promise<void>;
  getDocumentTypesAction: () => Promise<string[]>;
  updateFileRepoColumnsAction: (data: Record<string, boolean>) => Promise<void>;

  getRecommendationsTableAction: (filters: IRecommendationDataFilters) => Promise<PagedRecommendations>;
  updateRecommendationStatusAction: (payload: {
    accepted_ids: number[];
    declined_ids: number[];
    new_ids: number[];
    filters?: IApproveMeasureFilters;
  }) => Promise<{ [key: string]: IRecommendationData }>;
  updateRecommendationAction: (data: {
    id: number;
    payload: Partial<IRecommendationItem & { split_by_commitments?: boolean }>;
  }) => Promise<IRecommendationData>;
  regenerateRecommendationDataAction: (fileId: number) => Promise<IRecommendationData>;
  removeFilesFromStoreAction: (fileIds: number[]) => void;
  getFilesDocumentTypesSuggestionsAction: (
    file_names: string[]
  ) => Promise<{ file_name: string; document_type: string }[]>;
  bulkUpdateFilesRepoAction: (data: IBulkUpdateFilesRepoData) => Promise<IFileRepo[]>;
  bulkUpdateWorkspaceFilesAction: (data: IBulkUpdateFilesRepoData) => Promise<IFileMetaDetail[]>;
}

interface FileActionsParams extends AsyncActionsParams {
  isSharedDeal: boolean;
}

export class FileActions extends AsyncActions implements IFileActions {
  protected readonly isSharedDeal: boolean;

  constructor({ isSharedDeal, ...params }: FileActionsParams) {
    super(params);
    this.isSharedDeal = isSharedDeal;
  }

  private TransformFileFiltersToReq = (data: Partial<FileRepoFilters>): FileFiltersToReq => {
    const result: FileFiltersToReq = {};
    const { deals, investors, document_types, source, date, show_latest } = data;

    if (deals?.length) {
      result.deal_ids = deals.map((item) => item.id);
    }

    if (investors?.length) {
      result.investor_ids = investors.map((item) => item.id);
    }

    if (document_types?.length) {
      result.document_types = document_types;
    }

    if (source?.length) {
      result.sources = source;
    }

    if (date) {
      const updatedDates: DateRange<string> = {};

      for (const d in date) {
        updatedDates[d as keyof DateRange] = formatDate({
          date: date[d as keyof DateRange],
          format: "YYYY-MM-DD"
        });
      }

      result.date = updatedDates;
    }

    if (show_latest) {
      result.show_latest = Number(show_latest);
    }

    // type RestFields = Pick<FileRepoFilters, "investors">;
    // for (const key in data) {
    //   const field = data[key as keyof RestFields];
    //   if (Array.isArray(field)) {
    //     if (!field.length) {
    //       continue;
    //     }
    //     result[key as keyof RestFields] = field.map((item) => item.id);
    //   } else if (field) {
    //     result[key as keyof RestFields] = field;
    //   }
    // }

    return result;
  };

  public handleFile = (file: IFile, event: IHandleItemEventType): void => {
    if (file.upload_type === "document-file" || file.upload_type === "document-image") {
      // this.dispatch({
      //   type: DocumentsAction.HandleDocumentFileSuccess,
      //   payload: { item: file, event }
      // });
      return;
    }
    this.dispatch({
      type: DealAction.HandleFileSuccess,
      payload: { item: file, event }
    });
  };

  public getFileAction = async ({ dealId, fileId }: GetFileData): Promise<IFile> => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IFile>>({
      url: `/api/v0.0.2/deals/${dealId}/files/${fileId}`
    });
    return data;
  };

  // request for a new branch structure
  public getFilesAction = async (dealId: number) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<Record<number, IFile>>>({
      url: this.isSharedDeal ? `/api/v0.0.3/shared-deals/${dealId}/files` : `/api/v0.0.3/deals/${dealId}/files`
    });
    this.dispatch({ type: DealAction.GetFilesSuccess, payload: data });
  };

  public getAllFilesByDRAction = async (queries: IGetDRFilesQueries): Promise<PagedDataroomFiles> => {
    const controller = this.addProcess("getDataAction");
    // Temporary use Array<{ name: string }> instead of simple string[], later it should be replaced with ids[] - numbers;
    const updatedArrayFilters: Partial<
      Record<keyof Pick<DataroomFilesFilters, "accounts" | "altiusDeals" | "docTypes" | "deals">, { name: string }[]>
    > = {};

    for (const key in queries.filters) {
      const values = queries.filters[key as keyof DataroomFilesFilters];
      if (key.includes("dataroomsIds")) continue;
      if (Array.isArray(values)) {
        updatedArrayFilters[
          key as keyof Pick<DataroomFilesFilters, "accounts" | "altiusDeals" | "docTypes" | "deals">
        ] = values.map((item) => ({ name: item.toString() }));
      }
    }

    const {
      data: { data }
    } = await this.api.get<AltiusRes<PagedDataroomFiles>>({
      url: "/api/v0.0.3/get-datarooms/all-datarooms-files-from-deals",
      signal: controller.signal,
      params: { ...queries, filters: { ...queries.filters, ...updatedArrayFilters } }
    });
    this.removeProcess("getDataAction");
    return data;
  };

  public downloadFileAction = async (data: IDownloadFileData): Promise<void> => {
    const { fileId, dealId } = data;
    // Get Response from API
    return this.api
      .get({
        url: this.isSharedDeal
          ? `/api/v0.0.3/shared-deals/${dealId}/files/${fileId}/save`
          : `/api/v0.0.2/deals/files/${fileId}/save`,
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public uploadFileAction: IFileActions["uploadFileAction"] = async ({ onDispatch = true, ...data }) => {
    try {
      const uuid = uuidv4();
      const { deal_id, isGlobalWorkspace, file, isSaveUserInSurvey, ...rest } = data;
      const url = isGlobalWorkspace
        ? "/api/v0.0.3/global-workspace/files"
        : this.isSharedDeal
          ? `/api/v0.0.3/shared-deals/${deal_id}/files`
          : `/api/v0.0.2/deals/${deal_id}/files`;
      const {
        data: { data: fileWithUser }
      } = await this.api.post<AltiusRes<CreateFileRes>>({
        url,
        data: toFormData({ ...rest, file }),
        headers: {
          "Content-Type": "multipart/form-data"
        },
        onUploadProgress: (progressEvent) => {
          this.dispatch({
            type: DealAction.UploadLoadingProgress,
            payload: {
              event: progressEvent,
              fileName: file.name,
              uuid
            }
          });
        }
      });
      this.dispatch({
        type: DealAction.UploadLoadingSuccess,
        payload: uuid
      });

      const { user, ...fileFromServer } = fileWithUser;
      // If this is request of the form(question) save user to the survey users
      if (isSaveUserInSurvey && user) {
        this.dispatch({ type: DealAction.AddUserDealForm, payload: user });
      }
      onDispatch && this.handleFile(fileFromServer, "add"); // added this parameter by default true, but in case we don't need to dispatch document attachments

      return fileFromServer;
    } catch (error) {
      this.dispatch({ type: DealAction.UploadLoadingFailure, payload: null });
      throw error;
    }
  };

  public uploadCloudMirrorSectionFilesAction: IFileActions["uploadCloudMirrorSectionFilesAction"] = async (items) => {
    const formData = new FormData();
    items.forEach((item, index) => {
      formData.append(`items[${index}][file]`, item.file);
      item.deal_ids.forEach((dealId) => {
        formData.append(`items[${index}][deal_ids][]`, String(dealId));
      });
      formData.append(`items[${index}][document_type]`, item.document_type);
      if (item.investor_id != null) {
        formData.append(`items[${index}][investor_id]`, String(item.investor_id));
      }
      if (item.effective_date) {
        formData.append(`items[${index}][effective_date]`, item.effective_date);
      }
    });

    await this.api.post({
      url: "/api/v0.0.3/datarooms/upload-section-files",
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
  };

  public uploadDashboardFileAction = async (data: IUploadFileData): Promise<string | undefined> => {
    try {
      const uuid = uuidv4();
      const { deal_id, file, onUploadProgress, signal, ...rest } = data;

      const {
        data: { data: fileWithUser }
      }: AxiosResponse<AltiusRes<CreateFileRes>> = await this.api.post({
        url: `/api/v0.0.2/deals/${deal_id}/files`,
        data: toFormData({ ...rest, file }),
        onUploadProgress(progressEvent) {
          onUploadProgress && onUploadProgress(progressEvent);
        },
        signal
      });
      this.dispatch({
        type: DealAction.UploadLoadingSuccess,
        payload: uuid
      });

      const { ...fileFromServer } = fileWithUser;
      this.handleFile(fileFromServer, "add");
      if (fileFromServer.state === "scanning") {
        return "We are scanning files for malware, files will be available soon.";
      }
    } catch (error) {
      this.dispatch({ type: DealAction.UploadLoadingFailure, payload: null });
      throw error;
    }
  };

  public dispatchFileAction = (file: IFile): void => {
    this.handleFile(file, "store");
  };

  public updateFileAction = async (data: IUpdateFileData): Promise<IFile> => {
    const { fileId, ...body } = data;
    const {
      data: { data: file }
    } = await this.api.patch<AltiusRes<IFile>>({
      url: `/api/v0.0.3/files/${fileId}`,
      data: body
    });
    this.handleFile(file, "store");
    return file;
  };

  public updateFileRepoAction = async (data: IUpdateFileData): Promise<IFileRepo> => {
    const { fileId, ...body } = data;
    const {
      data: { data: file }
    } = await this.api.patch<AltiusRes<IFileRepo>>({
      url: `/api/v0.0.3/files/${fileId}`,
      data: { ...body, files_table_request: true }
    });
    return file;
  };

  public updateFileRepoColumnsAction = async (data: Record<string, boolean>): Promise<void> => {
    await this.api.post({
      url: "/api/v0.0.3/user/settings",
      data: { type: "fileRepoColumns", key: "fileRepoColumns", data }
    });

    this.dispatch({ type: UserAction.SetFileRepoColumns, payload: data });
  };

  public moveFileAction = async (data: IMoveFileData): Promise<IFile> => {
    const { file, sectionId, useMethod = "update" } = data;
    const {
      data: { data: updatedFile }
    } = await this.api.post<AltiusRes<IFile>>({
      url: `/api/v0.0.2/deals/${file.deal_id}/files/${file.id}/move/sections/${sectionId}`
    });
    this.handleFile(updatedFile, useMethod);
    // this.handleFile(file, "delete");
    // this.handleFile(updatedFile, "add");
    return updatedFile;
  };

  // public moveFileFromEmailAction = async (
  //   data: IMoveFileData
  // ): Promise<IFile> => {
  //   try {
  //     const { file, sectionId } = data;
  //     const {
  //       data: { data: updatedFile },
  //     }: AxiosResponse<{ data: IFile }> = await this.api.post({
  //       url: `/api/v0.0.2/deals/${file.deal_id}/files/${file.id}/move/sections/${sectionId}`,
  //     });
  //     await this.getSections({ dealId: file.deal_id });
  //     return updatedFile;
  //   } catch (error) {
  //     throw error;
  //   }
  // };

  public deleteFileAction = async (file: IFile): Promise<void> => {
    const { deal_id, id } = file;

    await this.api.delete({
      url: this.isSharedDeal
        ? `/api/v0.0.3/shared-deals/${deal_id}/files/${id}`
        : deal_id
          ? `/api/v0.0.2/deals/${deal_id}/files/${id}`
          : `/api/v0.0.3/global-workspace/files/${id}`
    });
    this.handleFile(file, "delete");
  };

  public getFileMetaAction = async (dealId: number, fileId: number): Promise<void> => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<IFileMeta>>({
      url: `/api/v0.0.2/deals/${dealId}/files/${fileId}`
    });
    this.dispatch({ type: DealAction.GetFileMetaSuccess, payload });
  };

  public updateFileMetaAction = (payload: Partial<IFileMeta>): void => {
    this.dispatch({ type: DealAction.UpdateFileMetaSuccess, payload });
  };

  public invalidateFileMetaAction = (): void => {
    this.dispatch({ type: DealAction.InvalidateFileMeta, payload: null });
  };

  // Move file from datarooms to S3
  public downloadFileFromDRAction = async <R extends DownloadDRFile | undefined = "object">({
    dealId,
    fileId,
    response_type = "object"
  }: {
    dealId: number;
    fileId: number;
    response_type?: R;
  }) => {
    const response = await this.api.get<R extends "object" ? IFile : BlobPart>({
      url: `/api/v0.0.2/deals/${dealId}/files/${fileId}/download`,
      params: { response_type },
      ...(response_type === "blob" && { headers: { responseType: "blob" } })
    });

    if (response_type === "blob") {
      return downloadFileAxios(response as AxiosResponse<BlobPart>) as R extends "object" | undefined ? IFile : void;
    }

    return (response as AxiosResponse<IFile>).data as R extends "object" | undefined ? IFile : void;
  };

  public uploadDirectoryAction: IFileActions["uploadDirectoryAction"] = async ({ deal_id, files, section_id }) => {
    const fileObject = Array.from(files).reduce((acc, file) => {
      return { ...acc, [file.webkitRelativePath]: file };
    }, {});

    try {
      const uuid = uuidv4();

      await this.api.post({
        url: `/api/v0.0.3/deals/${deal_id}/files-directory`,
        data: toFormData(fileObject),
        ...(section_id && { params: { section_id } }),
        headers: {
          "Content-Type": "multipart/form-data"
        },
        onUploadProgress: (progressEvent) => {
          this.dispatch({
            type: DealAction.UploadLoadingProgress,
            payload: {
              event: progressEvent,
              fileName: "Directory",
              uuid
            }
          });
        }
      });

      this.dispatch({
        type: DealAction.UploadLoadingSuccess,
        payload: uuid
      });
    } catch (error) {
      this.dispatch({ type: DealAction.UploadLoadingFailure, payload: null });
      throw error;
    }
  };

  public extractFileK1Action: IFileActions["extractFileK1Action"] = async (id) => {
    await this.api.get({ url: `/api/v0.0.3/files/${id}/k1` });
  };

  public getFilesRepoAction: IFileActions["getFilesRepoAction"] = async ({
    page = 1,
    per_page = 50,
    filters,
    sort
  }) => {
    const transformedFilters = this.TransformFileFiltersToReq(filters);
    const {
      data: { data }
    } = await this.api.post<AltiusRes<{ total: number; last_page: number; current_page: string; files: IFileRepo[] }>>({
      url: "/api/v0.0.3/files/all",
      data: {
        page,
        per_page,
        sort,
        ui_filters: filters, //API will save ui filters to the user settings, difference between filters and ui_filters is that in includes deals & investors as objects with name & id
        ...transformedFilters
      }
    });
    this.dispatch({ type: UserAction.SetFilesFilters, payload: filters });
    return data;
  };

  public downloadFilesRepoAction = async (file_ids: number[]): Promise<void> => {
    return this.api
      .get({
        url: "/api/v0.0.3/files/download",
        responseType: "blob",
        params: {
          file_ids
        }
      })
      .then(downloadFileAxios);
  };

  public createFileRepoTaggerLinkAction = async (file_ids: number[]): Promise<void> => {
    await this.api.post<AltiusRes<{ link: string }>>({
      url: "/api/v0.0.3/files/create-tagger-link",
      data: {
        file_ids
      }
    });
  };

  public extractFileRepoK1Action: IFileActions["extractFileRepoK1Action"] = async (ids) => {
    await this.api.get({
      url: "/api/v0.0.3/files/extract-k1",
      params: {
        file_ids: ids
      }
    });
  };

  public getFilePolygonsAction: IFileActions["getFilePolygonsAction"] = async (fileId) => {
    return this.api
      .get<AltiusRes<Polygons>>({ url: `/api/v0.0.3/files/${fileId}/extraction/polygons` })
      .then((res) => res.data.data);
  };

  public getFileTagsAction: IFileActions["getFileTagsAction"] = async (fileId) => {
    return this.api
      .get<AltiusRes<IDataExtractionTag[]>>({ url: `/api/v0.0.3/files/${fileId}/extraction/tags` })
      .then((res) => res.data.data);
  };

  public createFileTagAction: IFileActions["createFileTagAction"] = async (fileId, data) => {
    return this.api
      .post<AltiusRes<IDataExtractionTag>>({ url: `/api/v0.0.3/files/${fileId}/extraction/tags`, data })
      .then((res) => res.data.data);
  };

  public updateFileTagAction: IFileActions["updateFileTagAction"] = async (fileId, tagId, data) => {
    return this.api
      .patch<AltiusRes<IDataExtractionTag>>({ url: `/api/v0.0.3/files/${fileId}/extraction/tags/${tagId}`, data })
      .then((res) => res.data.data);
  };

  public deleteFileTagAction: IFileActions["deleteFileTagAction"] = async (fileId, tagId) => {
    await this.api.delete({ url: `/api/v0.0.3/files/${fileId}/extraction/tags/${tagId}` });
  };

  public saveFileTagsAction: IFileActions["saveFileTagsAction"] = async (fileId) => {
    await this.api.post({ url: `/api/v0.0.3/files/${fileId}/extraction/tags/save` });
  };

  public getFileByIdAction: IFileActions["getFileByIdAction"] = async (fileId) => {
    return this.api.get<AltiusRes<IFileRepo>>({ url: `/api/v0.0.3/files/${fileId}` }).then((res) => res.data.data);
  };

  public checkFilesVerifiedAction: IFileActions["checkFilesVerifiedAction"] = async (fileIds) => {
    await this.api.post({ url: "/api/v0.0.3/files/check-verified", data: { file_ids: fileIds } });
  };

  public getDocumentTypesAction: IFileActions["getDocumentTypesAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<string[]>>({
      url: "/api/v0.0.3/files/document-types"
    });

    this.dispatch({ type: DealAction.GetDocumentTypesSuccess, payload: data });

    return data;
  };

  public fileAiExtractionAction: IFileActions["fileAiExtractionAction"] = async (file_ids) => {
    await this.api.post<AltiusRes<IFile>>({
      url: "/api/v0.0.3/files/extract-files",
      data: {
        file_ids
      }
    });
  };

  public reRunFileExtractionAction: IFileActions["reRunFileExtractionAction"] = async ({ file_id, prompt }) => {
    await this.api.post({ url: `/api/v0.0.3/files/${file_id}/extraction/rerun`, data: { prompt } });
  };

  public uploadFilesWithAiAction: IFileActions["uploadFilesWithAiAction"] = async (data) => {
    const { dealId, files, aiExtraction, isK1Extraction } = data;
    const filesArray = Array.from(files);
    const formData = new FormData();
    filesArray.forEach((file, index) => {
      formData.append(`files[${index}]`, file);
    });

    // Add aiExtraction parameter (always send boolean value)
    formData.append("extract_with_ai", aiExtraction ? "1" : "0");

    if (isK1Extraction) {
      formData.append("is_k1_extraction", "1");
    }

    await this.api.post<AltiusRes<IFile[]>>({
      url: `/api/v0.0.3/deals/${dealId}/ai-files`,
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
  };

  public getRecommendationsTableAction: IFileActions["getRecommendationsTableAction"] = async (filters) => {
    const {
      data: { data: dataResponse }
      //without uiId
    } = await this.api.post<AltiusRes<PagedRecommendations>>({
      url: "/api/v0.0.3/datarooms/recommendations",
      data: filters
    });

    return dataResponse;
  };

  public updateRecommendationStatusAction: IFileActions["updateRecommendationStatusAction"] = async ({
    accepted_ids,
    declined_ids,
    new_ids,
    filters
  }) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<{ [key: string]: IRecommendationData }>>({
      url: "/api/v0.0.3/datarooms/recommendations/save",
      data: { accepted_ids, declined_ids, new_ids, ...(filters && { filters: [filters] }) }
    });

    return data;
  };

  public updateRecommendationAction: IFileActions["updateRecommendationAction"] = async ({ id, payload }) => {
    const {
      data: { data }
    } = await this.api.patch<AltiusRes<IRecommendationData>>({
      url: `/api/v0.0.3/datarooms/recommendations/${id}`,
      data: payload
    });

    return data;
  };

  public regenerateRecommendationDataAction: IFileActions["regenerateRecommendationDataAction"] = async (fileId) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<IRecommendationData>>({
      url: `/api/v0.0.3/datarooms/recommendations/regenerate/${fileId}`
    });
    return data;
  };

  public removeFilesFromStoreAction: IFileActions["removeFilesFromStoreAction"] = async (fileIds) => {
    this.dispatch({ type: DealAction.RemoveFilesFromStore, payload: fileIds });
  };

  public getFilesDocumentTypesSuggestionsAction: IFileActions["getFilesDocumentTypesSuggestionsAction"] = async (
    file_names
  ) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<{ file_name: string; document_type: string }[]>>({
      url: "/api/v0.0.3/files/document-types/ai-suggest",
      data: { file_names }
    });
    return data;
  };

  public bulkUpdateFilesRepoAction: IFileActions["bulkUpdateFilesRepoAction"] = async (data) => {
    const {
      data: { data: files }
    } = await this.api.post<AltiusRes<IFileRepo[]>>({
      url: "/api/v0.0.3/files/bulk-update",
      data: { ...data, files_table_request: true }
    });
    return files;
  };

  public bulkUpdateWorkspaceFilesAction: IFileActions["bulkUpdateWorkspaceFilesAction"] = async (data) => {
    const {
      data: { data: files }
    } = await this.api.post<AltiusRes<IFileMetaDetail[]>>({
      url: "/api/v0.0.3/files/bulk-update",
      data
    });
    return files;
  };
}
